<template>
  <div class="post-purchase-modal">
    <HMModalHeader
      v-if="!hideHeader"
      :title="$t('PRODUCTS.TITLE.STRIPE_PAYMENT_MODAL_TITLE')"
      @close="close"
    />
    <div
      id="card-element"
      ref="card-element"
      :class="{ 'is-invalid': stripeError }"
    ></div>
    <div class="invalid-feedback" v-if="stripeError">
      {{ stripeError }}
    </div>

    <HMButton
      class="w-100 text-center mt-2"
      color="brand"
      :disabled="!stripeCompleted || inProgress"
      :loading="inProgress"
      @click="submit"
    >
      <span class="w-100 text-center">
        {{ buttonText }}
      </span>
    </HMButton>
  </div>
</template>

<script>
import { style } from '@/utils/stripe';

export default {
  name: 'StripePaymentModal',
  props: {
    user: {
      type: Object,
    },
    product: {
      type: Object,
    },
    stripePublishableKey: {
      type: String,
    },
    setupIntent: {
      type: Object,
    },
    paymentIntent: {
      type: Object,
    },
    coupon: {
      type: String,
    },
    studioURL: {
      type: String,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
    },
  },
  computed: {
    isFreeProduct() {
      return this.product.pricing_type === 'free';
    },
    returnUrl() {
      const route = this.$router.resolve({
        name: 'studio-product-checkout-stripe-confirm',
        params: { product_id: this.product.id },
      });

      return `${window.location.origin}${route.href}`;
    },
  },
  data() {
    return {
      stripeError: false,
      elements: null,
      stripeCompleted: false,
      inProgress: false,
    };
  },
  methods: {
    async submit() {
      if (this.inProgress) return;
      this.inProgress = true;
      try {
        if (this.setupIntent?.id) {
          const confirmSetup = await this.stripe.confirmSetup({
            elements: this.elements,
            confirmParams: {
              return_url: this.returnUrl,
              payment_method_data: {
                billing_details: {
                  name: `${this.user.student.first_name} ${this.user.student.last_name}`,
                  email: this.user.email,
                },
              },
            },
          });
          console.log('confirmSetup result:', confirmSetup);
          if (confirmSetup.error) {
            this.paymentError = true;
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Payment failed',
              text: confirmSetup.error.message,
              duration: -1, // stay ON
            });
          }
        } else {
          const payment = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
              return_url: this.returnUrl,
              payment_method_data: {
                billing_details: {
                  name: `${this.user.student.first_name} ${this.user.student.last_name}`,
                  email: this.user.email,
                },
              },
            },
          });
          console.log('payment result:', payment);
          if (payment.error) {
            this.paymentError = true;
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Payment failed',
              text: payment.error.message,
              duration: -1, // stay ON
            });
          }
        }
      } catch (error) {
        this.paymentError = true;
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Payment failed',
          text: error,
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
    async initStripe() {
      this.stripe = window.Stripe(this.stripePublishableKey);
      if (this.setupIntent?.id) {
        this.elements = this.stripe.elements({
          clientSecret: this.setupIntent.client_secret,
        });
      } else {
        this.elements = this.stripe.elements({
          clientSecret: this.paymentIntent.client_secret,
        });
      }
      this.card = this.elements.create('payment', {
        style,
        hidePostalCode: true,
        fields: {
          billingDetails: {
            name: 'never',
            email: 'never',
          },
        },
      });
      this.card.addEventListener('change', this.stripeChangeCallback);
      const mountTimer = setInterval(
        () => {
          console.log('Waiting for stripe card element');
          if (this.$refs['card-element']) {
            this.card.mount(this.$refs['card-element']);
            console.log('Stripe card element mounted');
            clearInterval(mountTimer);
          }
        },
        50,
        100
      );
    },
    stripeChangeCallback(event) {
      if (event.complete) {
        this.stripeCompleted = true;
        this.stripeError = false;
      } else if (event.error) {
        this.stripeCompleted = false;
        this.stripeError = event.error.message;
      }
    },
    close() {
      if (this.onClose) this.onClose();
      this.$attrs._close()
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initStripe();
    });
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/components-variables.scss';
</style>
